"use client";

import { UserCog } from "lucide-react";
import { Controller, useForm } from "react-hook-form";
import { Button } from "../../elements/button/button";
import { Switch } from "../../elements/switch/switch";
import { Text } from "../../elements/text/text";
import type { UserRole } from "../user-card-profile/user-card-profile";

export interface UserPreferencesFormData {
  priceVisibility: boolean;
}

export interface UserCardPreferencesProps {
  microcopies: {
    title: string;
    headerDescription: string;
    priceVisibilityLabel: string;
    hideLabel: string;
    showLabel: string;
    saveButtonLabel: string;
  };
  defaultPriceVisibility?: boolean;
  onSubmit?: (data: UserPreferencesFormData) => void;
  isPending?: boolean;
  userRole?: UserRole;
}

export function UserCardPreferences(props: Readonly<UserCardPreferencesProps>) {
  const {
    control,
    handleSubmit,
    formState: { isDirty },
  } = useForm<UserPreferencesFormData>({
    values: {
      priceVisibility: props.defaultPriceVisibility ?? false,
    },
    mode: "onChange",
  });

  return (
    <article className="flex flex-col gap-6 rounded-md bg-white p-4">
      {/* Header Section */}
      <header className="flex flex-col gap-1">
        {/* Title with Icon */}
        <div className="flex items-center gap-2">
          <UserCog className="h-6 w-6 text-zinc-500" />
          <Text variant="h4">{props.microcopies.title}</Text>
        </div>

        {/* Header Description */}
        <div className="flex flex-col gap-1">
          <Text color="black" variant="body-md">
            {props.microcopies.headerDescription}
          </Text>
        </div>
      </header>

      {/* Content Section - Form */}
      <form
        className="flex flex-col gap-6"
        onSubmit={handleSubmit((data) => props.onSubmit?.(data))}
      >
        {/* Price Visibility Switch */}
        <div className="flex flex-col gap-2">
          <Text variant={"body-sm-bold"}>
            {props.microcopies.priceVisibilityLabel}
          </Text>
          <div className="flex items-center gap-2">
            <Text variant="body-sm">{props.microcopies.hideLabel}</Text>
            <Controller
              control={control}
              name="priceVisibility"
              render={({ field }) => (
                <Switch
                  checked={field.value}
                  className="data-[state=checked]:bg-black [&_span]:bg-white"
                  disabled={props.userRole === "SHOP_VIEWER"}
                  onCheckedChange={field.onChange}
                />
              )}
            />
            <Text variant="body-sm">{props.microcopies.showLabel}</Text>
          </div>
        </div>

        {/* Footer Section */}
        <footer className="flex items-center pt-12">
          <Button
            disabled={!isDirty || props.isPending}
            loading={props.isPending}
            size="sm"
            type="submit"
            variant="default"
          >
            {props.microcopies.saveButtonLabel}
          </Button>
        </footer>
      </form>
    </article>
  );
}
